import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { sampleFile } from '../clone/sample-file';
import { SampleAudio } from '../clone/SampleAudio';
import { useRecorder } from '../clone/use-recorder';
import { timeLabel } from '../clone/clone-errors';
import { recordingWav } from './recording-wav';
import { uiFormat, uiText } from '../../i18n';

export function SpeechAudioInput({
  file,
  disabled,
  accept,
  maximumMs,
  maximumBytes = 20 * 1024 * 1024,
  onFile,
}: {
  file: File | null;
  disabled: boolean;
  accept: string[];
  maximumMs: number;
  maximumBytes?: number;
  onFile(file: File): void;
}) {
  const [error, setError] = useState(''),
    [reading, setReading] = useState(false),
    [duration, setDuration] = useState<{ file: File; milliseconds: number } | null>(null);
  const mounted = useRef(true);
  const generation = useRef(0);
  const validation = useRef<AbortController | null>(null);
  const cancelValidation = useCallback(() => { generation.current++; validation.current?.abort(); }, []);
  useEffect(
    () => { mounted.current = true; return () => {
      mounted.current = false; cancelValidation();
    }; },
    [cancelValidation],
  );
  async function select(input: File, recording = false) {
    if (disabled) return;
    const current = ++generation.current;
    validation.current?.abort();
    const request = new AbortController(); validation.current = request;
    setReading(true);
    setError('');
    try {
      const audio = recording ? await recordingWav(input, maximumMs) : sampleFile.confirmAudioType(input);
      if (audio.size > maximumBytes || !accept.includes(audio.type))
        throw new Error(uiFormat('Выберите поддерживаемый аудиофайл до {0} МБ.', maximumBytes / 1024 / 1024));
      const milliseconds = await sampleFile.readDurationMs(audio, request.signal);
      if (milliseconds < 1000 || milliseconds > maximumMs)
        throw new Error(uiFormat('Нужна запись от 1 секунды до {0}.', timeLabel(maximumMs)));
      if (!request.signal.aborted && generation.current === current) {
        setDuration({ file: audio, milliseconds });
        onFile(audio);
      }
    } catch (failure) {
      if (!request.signal.aborted && generation.current === current) setError(failure instanceof Error ? failure.message : 'Не удалось прочитать запись.');
    } finally {
      if (mounted.current && validation.current === request) setReading(false);
    }
  }
  const recorder = useRecorder(file => {
    void select(file, true);
  }, maximumMs);
  const cancelRecording = useRef(recorder.cancel);
  useEffect(() => { cancelRecording.current = recorder.cancel; }, [recorder.cancel]);
  useLayoutEffect(() => {
    if (!disabled) return;
    cancelValidation(); cancelRecording.current();
  }, [disabled, cancelValidation]);
  useEffect(() => {
    if (!file) return;
    const request = new AbortController();
    void sampleFile.readDurationMs(file, request.signal).then(milliseconds => {
      if (!request.signal.aborted) setDuration({ file, milliseconds });
    }).catch(() => {});
    return () => request.abort();
  }, [file]);
  const blocked = disabled || reading || recorder.state !== 'idle';
  return (
    <div className="speech-input">
      <label>
        {uiText('Аудиозапись')}
        <input
          aria-label={uiText('Аудиозапись')}
          type="file"
          accept={accept.join(',')}
          disabled={blocked}
          onChange={event => {
            const next = event.target.files?.[0];
            if (next) void select(next);
            event.target.value = '';
          }}
        />
      </label>
      {recorder.state === 'recording' ? (
        <button type="button" onClick={recorder.stop}>
          {uiFormat('Остановить запись · {0}', timeLabel(recorder.elapsed))}
        </button>
      ) : (
        <button type="button" disabled={blocked} onClick={() => void recorder.start()}>
          {uiText(recorder.state === 'requesting' ? 'Включаем микрофон…' : 'Записать с микрофона')}
        </button>
      )}
      <small>{uiFormat('До {0} · до {1} МБ', timeLabel(maximumMs), maximumBytes / 1024 / 1024)}</small>
      {reading && <p role="status">{uiText('Читаем запись…')}</p>}
      {file && (
        <div>
          <p>
            {file.name} · {duration?.file === file ? timeLabel(duration.milliseconds) : '—'}
          </p>
          <SampleAudio file={file} />
        </div>
      )}
      {(error || recorder.error) && <p role="alert">{uiText(error || recorder.error)}</p>}
    </div>
  );
}
